/**
 * Single role table row for the Roles dashboard.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Role } from '../api/client';

interface RoleRowProps {
  role: Role;
  retrying: string | null;
  deletingRole: string | null;
  onRetry: (taskId: string) => void;
  onDeleteDraft: (roleId: string) => void;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'active': return 'status-badge status-active';
    case 'draft': return 'status-badge status-draft';
    case 'paused': return 'status-badge status-paused';
    case 'closed': return 'status-badge status-closed';
    default: return 'status-badge';
  }
};

const RoleRow: React.FC<RoleRowProps> = ({ role, retrying, deletingRole, onRetry, onDeleteDraft }) => {
  const navigate = useNavigate();

  return (
    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
      <td style={{ padding: '16px', verticalAlign: 'middle' }}>
        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
          {role.title}
        </div>
        {role.description && (
          <div style={{ fontSize: '14px', color: '#6b7280', maxWidth: '400px' }}>
            {role.description}
          </div>
        )}
      </td>
      <td style={{ padding: '16px', textAlign: 'left', verticalAlign: 'middle' }}>
        <span className={getStatusBadgeClass(role.status)}>
          {role.status}
        </span>
      </td>
      <td style={{ padding: '16px', textAlign: 'center', fontSize: '18px', fontWeight: 600, color: '#1f2937', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(role.is_processing || role.is_incomplete) && role.resumes_detected && role.resumes_detected > 0 ? (
              <span style={{ color: role.is_incomplete ? '#dc2626' : '#1f2937' }}>
                {Math.min(role.total_evaluations || 0, role.resumes_detected)} / {role.resumes_detected}
              </span>
            ) : (
              <span>{role.total_evaluations || 0}</span>
            )}
            {role.is_processing && !role.is_stuck && (
              <div className="processing-spinner" title="Processing resumes..."></div>
            )}
          </div>
          {(role.is_incomplete || role.is_stuck) && (
            <span style={{
              fontSize: '10px',
              color: '#dc2626',
              fontWeight: 600,
              padding: '2px 6px',
              background: '#fee2e2',
              borderRadius: '4px',
              whiteSpace: 'nowrap'
            }}>
              {role.is_stuck ? '\u23F1\uFE0F Stuck' : '\u26A0\uFE0F Incomplete'}
            </span>
          )}
          {role.failed_task_id && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              {!role.is_incomplete && !role.is_stuck && (
                <span style={{
                  fontSize: '11px',
                  color: '#dc2626',
                  fontWeight: 600,
                  padding: '2px 6px',
                  background: '#fee2e2',
                  borderRadius: '4px'
                }}>
                  \u26A0\uFE0F {role.failed_phase || 'Failed'}
                </span>
              )}
              <button
                onClick={() => onRetry(role.failed_task_id!)}
                disabled={retrying === role.failed_task_id}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  background: retrying === role.failed_task_id ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: retrying === role.failed_task_id ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#b91c1c')}
                onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#dc2626')}
              >
                {retrying === role.failed_task_id ? 'Retrying...' : '\uD83D\uDD04 Retry'}
              </button>
            </div>
          )}
        </div>
      </td>
      <td style={{ padding: '16px', textAlign: 'left', color: '#6b7280', fontSize: '14px', verticalAlign: 'middle' }}>
        {formatDate(role.last_evaluated_at)}
      </td>
      <td style={{ padding: '16px', textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
          {role.status === 'draft' ? (
            <>
              <button
                onClick={() => navigate(`/roles/new?role_id=${role.id}`)}
                style={{
                  padding: '8px 14px',
                  background: '#fbbf24',
                  color: '#78350f',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f59e0b'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fbbf24'}
              >
                Continue
              </button>
              <button
                onClick={() => onDeleteDraft(role.id)}
                disabled={deletingRole === role.id}
                style={{
                  padding: '8px 14px',
                  background: 'white',
                  color: '#dc2626',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: deletingRole === role.id ? 'not-allowed' : 'pointer',
                  opacity: deletingRole === role.id ? 0.6 : 1,
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#fef2f2')}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                {deletingRole === role.id ? 'Deleting...' : 'Delete'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate(`/roles/${role.id}/candidates`)}
                disabled={role.total_evaluations === 0 && !role.is_processing}
                style={{
                  padding: '8px 14px',
                  background: (role.total_evaluations === 0 && !role.is_processing) ? '#d1d5db' : '#2d5016',
                  color: (role.total_evaluations === 0 && !role.is_processing) ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: (role.total_evaluations === 0 && !role.is_processing) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#3d6b1f')}
                onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = '#2d5016')}
              >
                Candidates {role.is_processing && '(Processing)'}
              </button>
              <button
                onClick={() => navigate(`/roles/${role.id}/upload`)}
                style={{
                  padding: '8px 14px',
                  background: 'white',
                  color: '#2d5016',
                  border: '1px solid #2d5016',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2d5016';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#2d5016';
                }}
              >
                Upload
              </button>
              <button
                onClick={() => navigate(`/roles/${role.id}`)}
                style={{
                  padding: '8px 14px',
                  background: 'white',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                {role.is_criteria_locked && '\uD83D\uDD12 '}Criteria
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default RoleRow;
